// 2026-06-24: GlitchTip / Sentry error reporting. Must be the
// FIRST import so Sentry's `globalHandlers` integration (window
// "error" + "unhandledrejection") is installed before any other
// module can throw. See src/lib/sentry-bootstrap.ts for the
// no-op-when-VITE_GLITCHTIP_DSN-is-unset contract. Pinned by
// src/main.sentry.test.ts (asserts sentry-bootstrap is the first
// import in this file).
import "./lib/sentry-bootstrap.js";
// 2026-06-26: import reportError explicitly so the Vue
// app.config.errorHandler + window.unhandledrejection listener below
// can call it. The side-effect import above already installs the
// Sentry SDK, but the named import is what gives these handlers a
// type-safe reference.
import { reportError } from "./lib/sentry-bootstrap.js";
// PFP-1 §3.5 — second: must be imported before any dataLayer.push().
import "./lib/gtm-bootstrap.js";

import { createApp } from "vue";
import { createPinia } from "pinia";
import router from "./router";
import App from "./App.vue";
import { useAuthStore } from "./lib/auth";
import { initTheme } from "./lib/use-theme";
import "./styles/main.css";

initTheme();

const app = createApp(App);
const pinia = createPinia();

app.use(pinia);
app.use(router);

// 2026-06-26: forward Vue render errors to GlitchTip. Per the Sentry
// v10 docs (and the Sentry browser SDK's own setupOnce hooks), Vue
// render errors do NOT reach window.onerror — Vue catches them in
// its own error pipeline and replaces the component. The only path
// is app.config.errorHandler, which is the canonical Vue 3 hook.
// Without this, a render-time throw in PitchWizard or
// PitchWizardV2 would be invisible to GlitchTip even with the
// reportError() calls in the save flows. reportError is a no-op
// when VITE_GLITCHTIP_DSN is unset, so this handler is safe in
// dev. Pinned by src/__tests__/reportError-wiring.contract.test.ts
// case 5.
app.config.errorHandler = (err, _instance, info) => {
  reportError(err, { vueInfo: info, surface: "studio-vue-errorHandler" });
};

// 2026-06-26: defense in depth for unhandled promise rejections.
// Sentry's globalHandlers integration also catches these, but if a
// future SDK config change disables globalHandlers, the operator
// still sees the error. The listener is a thin pass-through to
// reportError. Pinned by
// src/__tests__/reportError-wiring.contract.test.ts case 6.
window.addEventListener("unhandledrejection", (e) => {
  reportError(e.reason, { kind: "unhandledrejection", surface: "studio-window" });
});

// Initialize auth before mounting so router guards have correct state
const auth = useAuthStore();
auth.init().finally(() => {
  app.mount("#app");
});